import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil, withLatestFrom, filter } from 'rxjs/operators';
import * as fromComm from 'src/app/modules/communications/store/communications';
import * as fromDataset from 'src/app/modules/management/store/datasets';
import { ConfiguredChannel, MassCommsContactDataset } from 'src/app/modules/communications/store/communications/communications.model';
import { cloneDeep } from 'lodash-es';
import { GUID_EMPTY } from '@app/core/constants';
import { FieldDataType } from 'src/app/modules/core/store/models';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Dataset, DatasetType } from 'src/app/modules/core/store/models/dataset.model';

@Component({
  selector: 'topo-everbridge-edit',
  templateUrl: './everbridge-edit.component.html',
  styleUrls: ['./everbridge-edit.component.scss'],
  standalone: false
})
export class EverbridgeEditComponent implements OnInit, OnDestroy {
  @Input() isActive: boolean;
  configuredChannel: ConfiguredChannel;
  destroy$ = new Subject<void>();
  emptyGuid = GUID_EMPTY;
  datasets: Dataset[] = [];
  configuredContactDatasetsFormGroup: FormGroup;

  constructor(
    private store: Store<fromComm.State>,
    private translate: TranslateService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.store.dispatch(fromDataset.loadDatasetsForSelect({ includeWorkflow: false }));

    this.store
      .select(fromComm.selectEverbridgeEditIsActive)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isActive => {
        this.isActive = isActive;
      });

    this.store
      .select(fromComm.selectSelectedConfiguredChannel)
      .pipe(
        withLatestFrom(this.store.select(fromComm.selectEverbridgeEditIsActive)),
        filter(([, isActive]) => isActive),
        takeUntil(this.destroy$)
      )
      .subscribe(([selectedChannel, isActive]) => {
        this.configuredChannel = !!selectedChannel ? cloneDeep(selectedChannel) : null;

        // Load field name dropdowns if the dataset is already selected on load
        if (this.configuredChannel && isActive) {
          if (!this.configuredChannel.settings.contactDatasets) {
            // For channels created before the contactDatasets array
            // Map to new array format and delete old config values
            this.configuredChannel.settings.contactDatasets = [
              {
                contactsDatasetUId: this.configuredChannel.settings.contactsDatasetUId,
                contactIdFieldUId: this.configuredChannel.settings.contactIdFieldUId
              }
            ];
          }

          // Remove old dataset settings if they exist
          delete this.configuredChannel.settings.contactsDatasetUId;
          delete this.configuredChannel.settings.contactIdFieldUId;

          this.mapConfiguredDatasetsToForm();
        }
      });

    this.store
      .select(fromDataset.selectDatasetsForSelect)
      .pipe(takeUntil(this.destroy$))
      .subscribe(ds => {
        if (ds) {
          this.datasets = ds.filter(d => d.type === DatasetType.Assets);
        }
      });

    this.configuredContactDatasetsFormGroup = this.fb.group({
      contactDatasets: this.fb.array([this.getContactDatasetForm(null, null)])
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.store.dispatch(fromComm.closeConfiguredChannelEdit());
  }

  cancelClick(): void {
    this.close();
  }

  saveClick(): void {
    // Map the contact datasets that have a dataset selected
    this.configuredChannel.settings.contactDatasets = this.contactDatasetsFormArray.value.filter(c => c.contactsDatasetUId);

    this.store.dispatch(fromComm.saveConfiguredChannelToApi({ channel: this.configuredChannel }));
  }

  mapConfiguredDatasetsToForm() {
    var configuredContactDatasets: MassCommsContactDataset[] = this.configuredChannel.settings.contactDatasets;

    this.configuredContactDatasetsFormGroup = this.fb.group({
      contactDatasets: this.fb.array(
        configuredContactDatasets.map(c => this.getContactDatasetForm(c.contactsDatasetUId, c.contactIdFieldUId))
      )
    });

    this.addContactDatasetIfEmpty();
  }

  addContactDatasetIfEmpty() {
    if (this.contactDatasetsFormArray.length === 0) {
      this.contactDatasetsFormArray.push(this.getContactDatasetForm(null, null));
    }
  }

  get contactDatasetsFormArray() {
    return this.configuredContactDatasetsFormGroup?.controls['contactDatasets'] as FormArray;
  }

  getDatasetContactIdFields(formGroup: FormGroup) {
    const datasetUId = formGroup.controls['contactsDatasetUId'].value;

    const dataset = this.datasets.find(d => d.uId === datasetUId);

    if (!datasetUId || !dataset) {
      return;
    }

    const textFields = dataset.schema.fields.filter(f => f.dataType === FieldDataType.Text || f.dataType === FieldDataType.Number);

    return textFields;
  }

  datasetUpdated(formGroup: FormGroup) {
    const datasetUId = formGroup.controls['contactsDatasetUId'].value;
    const dataset = this.datasets.find(d => d.uId === datasetUId);
    const textFields = dataset.schema.fields.filter(f => f.dataType === FieldDataType.Text || f.dataType === FieldDataType.Number);
    formGroup.controls['contactIdFieldUId'].patchValue(textFields.length > 0 ? textFields[0]?.uId : null);
  }

  deleteContactsDataset(index: number) {
    this.contactDatasetsFormArray.removeAt(index);
    this.addContactDatasetIfEmpty();
  }

  addContactsDataset() {
    this.contactDatasetsFormArray.push(this.getContactDatasetForm(null, null));
  }

  getContactDatasetForm(contactsDatasetUId?: string, contactIdFieldUId?: string): FormGroup {
    return this.fb.group({
      contactsDatasetUId: [contactsDatasetUId],
      contactIdFieldUId: [contactIdFieldUId]
    });
  }
}
